import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  Field,
  Badge,
  Button,
  cn,
  linkTextClasses,
  MonthStrip,
  type MonthStripItem,
  type MonthStripTone,
} from '@evogard/ui';
import { businessDate, loadFailureMessage } from '@evogard/domain';
import type {
  AwardLine,
  AwardState,
  WirePlateClassification,
  Period,
  ReasonNotToSettle,
  CommissionPayment,
  WireConsultantProfile,
} from '@evogard/contract';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { PROFILE_LABEL } from '../../lib/consultant-profile.js';

/**
 * O vocabulário do comercial na interna, num lugar só.
 *
 * ## Por que este arquivo é maior que o dos outros módulos
 *
 * Porque este é o módulo que mostra **quanto uma pessoa vai receber**, e a
 * frase que ele mais repete não é um rótulo: é a explicação de por que um número
 * não existe. Sete telas dizem isso — o painel, as vendas, as metas, o ranking,
 * as comissões, o cadastro e a régua —, e nenhuma pode dizer diferente da outra.
 *
 * Escrita em cada arquivo, "esta base ainda não tem régua" vira "nenhuma
 * comissão" numa tela e "não configurado" na outra, e quem lê as duas conclui
 * coisas diferentes sobre o próprio salário. É a mesma razão do `comum.tsx` da
 * cobrança, com uma consequência a mais: aqui a divergência tem valor em reais.
 *
 * ## O que este arquivo NÃO faz
 *
 * Não calcula nada. Não há uma soma, uma contagem de placa, uma faixa ou um
 * degrau escrito aqui — nem como constante, nem como valor sugerido em campo. A
 * aritmética inteira mora em `@evogard/domain` e é executada no servidor; o que
 * chega à tela é resultado, e o que a tela faz com ele é escrever.
 *
 * A única conta desta pasta é a **soma da exportação**, e ela nem é conta: é o
 * `total` que o servidor mandou, impresso ao lado das linhas que ele somou.
 *
 * ## E não traduz motivo em `switch` espalhado
 *
 * `REASONS_NOT_TO_SETTLE` é vocabulário fechado no contrato exatamente para a
 * tela ter **um** mapa de tradução. `REASON`, abaixo, é esse mapa — e o
 * `satisfies Record<ReasonNotToSettle, …>` faz o motivo novo quebrar a
 * compilação em vez de aparecer na tela como um identificador cru.
 */

/* ------------------------------------------------------------------------- *
 * Dinheiro
 * ------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------- *
 * A competência — o mês, e o fuso que não pode ser esquecido
 * ------------------------------------------------------------------------- */

/**
 * A competência corrente, no fuso da casa.
 *
 * `new Date().toISOString().slice(0, 7)` em UTC joga o dia 1º para o mês
 * anterior a partir das 21h de Fortaleza — e é a mesma armadilha que o legado
 * resolve partindo a string à mão, com um comentário dizendo por quê (§4.3 do
 * levantamento). Aqui o fuso é declarado, e quem declara é `businessDate` de
 * `@evogard/domain`: a mesma função que o servidor usa para decidir a
 * competência de `GET /sales/numbers`.
 *
 * Sem isso, a tela abriria em fevereiro no dia 1º de março, à noite, e a pessoa
 * concluiria que março não teve venda nenhuma.
 */
export function currentPeriod(): Period {
  return businessDate(new Date()).slice(0, 7);
}

/**
 * A competência em que as telas de PREMIAÇÃO abrem: o mês ANTERIOR.
 *
 * Decidido em 14/10/2026: em outubro a casa olha — e paga — a premiação de
 * setembro, como o legado. Abrir no mês corrente mostrava um mês pela metade
 * (a recorrência de outubro no dia 14 tinha 224 boletos; a de setembro, 578),
 * e quem conferia contra o legado concluía que faltava boleto.
 *
 * Vale só para premiação (Painel de Gestão, Carteira, Extrato). Venda,
 * comissão, ranking e meta continuam no mês corrente: a comissão é devida na
 * data da venda (P2), e o ranking é do mês em curso.
 */
export function awardPeriod(): Period {
  const [year, month] = currentPeriod().split('-').map(Number) as [number, number];
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
}

/** `AAAA-MM` deslocado `n` meses. */
export function shiftPeriod(period: Period, n: number): Period {
  const [year, month] = period.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(year, month - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * O filtro das telas de PREMIAÇÃO: **Mês atual / Próximo mês**, pelo mês em que
 * o consultor RECEBE — como o legado (19/10/2026).
 *
 * O valor continua sendo a competência que a rota entende (o mês dos boletos
 * pagos): receber em outubro é a premiação de setembro. Até aqui a tela pedia a
 * competência num campo livre, e "setembro" no filtro mostrava o que se recebe
 * em outubro — lido como um mês atrasado.
 */
export function receiptField(help?: string) {
  const [now, next] = receiptOptions();
  const options = [
    { value: now.value, label: `Mês atual — recebe em ${periodLabel(now.receipt)}` },
    { value: next.value, label: `Próximo mês — recebe em ${periodLabel(next.receipt)}` },
  ];
  return {
    type: 'selecao' as const,
    name: 'period',
    label: 'Recebimento',
    required: true,
    options,
    help:
      help ??
      'O mês em que a premiação é paga. A recorrência conta os boletos pagos no mês anterior a ele.',
  };
}

/**
 * As duas opções de recebimento — o mês atual e o seguinte —, cada uma com a
 * competência que a rota entende (`value`) e o mês em que se recebe
 * (`receipt`). Em outubro: `2026-09` recebe em outubro, `2026-10` em novembro.
 */
export function receiptOptions(): [
  { value: Period; receipt: Period },
  { value: Period; receipt: Period },
] {
  const current = currentPeriod();
  return [
    { value: awardPeriod(), receipt: current },
    { value: current, receipt: shiftPeriod(current, 1) },
  ];
}

/** `2026-10` → "Outubro" — só o nome do mês, com a inicial maiúscula. */
export function monthName(period: Period): string {
  const name = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${period}-01T00:00:00Z`));
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** "Recebe em outubro de 2026" — o rótulo de uma competência pelo mês do pagamento. */
export function receiptLabel(period: Period): string {
  return periodLabel(shiftPeriod(period, 1));
}

/** O primeiro dia da competência, como `AAAA-MM-DD`. */
export function firstDayOfPeriod(period: Period): string {
  return `${period}-01`;
}

/**
 * `2026-03` → "março de 2026".
 *
 * `timeZone: 'UTC'` porque a data foi construída em UTC: sem isso, o navegador
 * a oeste de Greenwich lê `2026-03-01T00:00:00Z` como 29 de fevereiro e o mês
 * inteiro aparece com o nome errado — o mesmo erro de fuso de uma linha acima,
 * na direção oposta.
 */
export function periodLabel(period: Period): string {
  return new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${period}-01T00:00:00Z`));
}

/**
 * O campo de competência.
 *
 * `type="month"` é o controle certo — ele impede o dia de existir, que é
 * exatamente o que `periodSchema` decidiu ao tirar o dia da rede. Onde o
 * navegador não o implementa (o Firefox ainda o desenha como texto), o campo
 * continua funcionando: a pessoa digita `AAAA-MM`, o rótulo de ajuda diz o
 * formato, e quem recusa o resto é o servidor.
 *
 * Não há validação de formato escrita aqui. Uma checagem no navegador daria a
 * mensagem "melhor" em 99% dos casos e, no 1%, diria que está tudo bem sobre
 * uma competência que o servidor vai recusar — é a mesma disciplina que a régua
 * da cobrança aplica à sobreposição.
 */
export function PeriodField({
  label = 'Competência',
  value,
  onChange,
  help,
}: {
  label?: string;
  value: Period;
  onChange: (value: Period) => void;
  help?: ReactNode;
}) {
  return (
    <Field
      label={label}
      type="month"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="AAAA-MM"
      help={help ?? 'O mês da competência, no formato AAAA-MM.'}
    />
  );
}

/* ------------------------------------------------------------------------- *
 * A fileira de filtros — o que fica na altura dos campos
 * ------------------------------------------------------------------------- */

/**
 * A célula de um controle SEM rótulo, na linha dos que têm.
 *
 * ## O defeito que ela conserta
 *
 * `items-end` numa fileira de campos alinha pela borda de baixo da **célula**,
 * e a célula de um `Field` com `help` tem uma linha de texto a mais embaixo.
 * Então a caixa de marcar e o botão desciam a altura daquela linha em relação
 * ao campo ao lado, e a fileira lia como duas fileiras desencontradas. Eram
 * cinco casos neste módulo: o “Só as sem consultor” das vendas, o “Mostrar
 * arquivados” do caixa, o “Exportar” das comissões, o “Abrir vínculo” da
 * hierarquia e o “Remover” de cada degrau da régua.
 *
 * O vão é reservado com a **mesma tipografia do rótulo de verdade** —
 * `text-sm font-medium`, o que `Field` e `Select` põem no `<label>`. Se ela
 * mudar, o vão acompanha. Um `pt-7` no lugar seria um número que ninguém
 * consegue ligar de volta ao rótulo que ele imita, e que envelhece calado.
 */
export function FieldSlot({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* o rótulo que este controle não tem — dele fica só a altura */}
      <span aria-hidden="true" className="text-sm font-medium">
        &nbsp;
      </span>
      <div className="flex min-h-10 items-center pointer-coarse:min-h-11">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * A classificação da placa
 * ------------------------------------------------------------------------- */

/**
 * O nome de cada classificação na tela, escrito **uma vez**.
 *
 * A lista fechada é do contrato (`PLATE_CLASSIFICATIONS`), e o
 * `satisfies Record<WirePlateClassification, string>` faz o valor novo
 * quebrar a compilação em vez de aparecer como `bronze` cru numa tela e
 * "Plano Bronze" na outra — que é como a contagem de Bronze, a que escolhe a
 * faixa das demais, passaria a depender de qual tela alguém abriu.
 *
 * Aqui só há rótulo. O que cada uma faz com o dinheiro — `bronze` paga por fora
 * da faixa, `reativacao` não conta para o consultor comum — é aritmética, mora
 * em `@evogard/domain` e roda no servidor.
 */
// @valor
export const CLASSIFICATION_LABEL = {
  venda: 'Venda',
  troca: 'Troca',
  bronze: 'Plano Bronze',
  reativacao: 'Reativação',
} as const satisfies Record<WirePlateClassification, string>;

/* ------------------------------------------------------------------------- *
 * Por que a casa ainda não produz o número
 *
 * O valor central deste módulo na tela. Cada entrada diz três coisas, e as três
 * são necessárias:
 *
 *   · **o que falta** — a frase que a pessoa lê primeiro;
 *   · **por que aquilo impede** — senão a ausência parece capricho, e alguém
 *     pede para "ligar assim mesmo";
 *   · **o que destrava** — a pergunta aberta ou a coluna que falta, nomeada. Um
 *     motivo sem dono é um motivo que ninguém resolve.
 * ------------------------------------------------------------------------- */

interface ReasonExplicado {
  /** O que falta, numa frase. */
  readonly title: string;
  /** Por que sem isso o número não sai. */
  readonly because: string;
  /** Quem responde, e o que acontece quando responder. */
  readonly unlock: string;
}

/**
 * O texto de um motivo, **para a tela que o mostra**.
 *
 * ## Por que o mapa único não bastou
 *
 * `REASON` foi escrito quando os motivos só apareciam na apuração, e a copy
 * dele é da apuração: fala em régua, em R$ 400,00 contra R$ 0,00, e diz que
 * "a competência inteira fica sem apurar".
 *
 * Quando o realizado da meta passou a sair (11/09), os mesmos motivos —
 * `venda_sem_classificacao`, e até 22/10/2026 `consultor_sem_perfil` —
 * passaram a aparecer na tela de metas, onde **as três frases são falsas**: a meta não paga nada, não
 * há régua nenhuma nela, e a recusa é **por alvo** e não da competência (três
 * consultores da GO mostram número ao lado de um que recusa). Era a tela
 * afirmando o que o código não cumpre, com o texto de outro módulo.
 *
 * O vocabulário continua **fechado e um só** — é o servidor que o nomeia, e
 * duas listas divergiriam na primeira mudança. O que passa a ser por tela é a
 * **explicação**, que é copy: o mesmo fato ausente trava coisas diferentes, e
 * dizer por que trava é a única parte que depende de quem pergunta.
 */
export type ReasonOverrides = Partial<Record<ReasonNotToSettle, ReasonExplicado>>;

// @valor
export const REASON = {
  /*
   * **`criterio_de_ranking_nao_definido` esteve aqui, e saiu em 09/09.** Era a
   * P5 — duas contagens rodando nos sistemas antigos, o mesmo consultor como 33
   * numa tela e 37 na outra. A casa escolheu venda + troca + Plano Bronze, e
   * escolheu que a contagem é uma só e não configuração por base: sem chave para
   * gravar, não há ausência de chave. O que recusa o ranking hoje são os dois
   * motivos abaixo, os mesmos da apuração.
   */
  regua_de_comissao_nao_definida: {
    title: 'Esta base não tem régua de comissão nesta competência.',
    because:
      'A régua é o que diz quanto vale cada placa em cada faixa, e ela é dado por ' +
      'base e por competência — reapurar março usa a régua de março. Sem as três ' +
      'réguas (consultor comum, base de reativação e Plano Bronze) a apuração não ' +
      'roda: faltando uma, o valor sairia menor com cara de conta certa.',
    unlock:
      'Cadastrar as três réguas desta praça em Comercial → Configuração → Régua ' +
      'de comissão, na competência que se quer apurar. Os números vêm do sistema ' +
      'antigo, onde as faixas são constantes escritas no código, e esperam o sim ' +
      'por escrito do responsável comercial — a tela da régua diz isso e não ' +
      'sugere valor nenhum.',
  },
  venda_sem_classificacao: {
    title: 'Há venda sem a classificação da placa.',
    because:
      'A classificação diz se a placa é venda, troca, Plano Bronze ou reativação, ' +
      'e é ela que decide o que conta e quanto paga. Venda sem classificação não ' +
      'entra em conta nenhuma — e adivinhar o tipo pelo texto da observação, como ' +
      'o sistema antigo faz, faria editar uma observação mudar quanto alguém recebe.',
    unlock:
      'Declarar a classificação em “Corrigir”, na tela de vendas — uma venda por ' +
      'vez, com o registro à frente de quem corrige. O histórico importado sai ' +
      'classificado de uma vez pela importação, que ainda não existe. Enquanto ' +
      'houver placa sem classificação atribuída a consultor, a competência ' +
      'inteira fica sem apurar.',
  },
  quinzena_nao_definida: {
    title: 'Não foi decidido qual quinzena paga o bônus.',
    because:
      'São duas janelas que quase não se encontram: o bônus paga sobre os dias 1 ' +
      'a 15, e o ranking que a operação consulta olha do dia 15 ao fim do mês. Um ' +
      'consultor com 52 vendas na segunda quinzena aparece em primeiro lugar na ' +
      'tela e leva R$ 0,00.',
    unlock:
      'A pergunta P9 do levantamento de remuneração. Enquanto ela estiver aberta, ' +
      'a bonificação não é um total — e a casa não a apresenta como se fosse.',
  },
  bonificacao_sem_registro: {
    title: 'Não há onde gravar uma bonificação apurada.',
    because:
      'Bonificação é coisa distinta da comissão, e o consultor recebe as duas. ' +
      'Sem tabela, ela só poderia ser recalculada a cada abertura de tela — que é ' +
      'o que o sistema antigo faz, e é como transferir uma venda em setembro muda ' +
      'a bonificação de março sem nenhuma tela avisar.',
    unlock:
      'A tabela de bonificação apurada, que não existe nesta onda. Ela espera a ' +
      'P9: congelar o cálculo antes de saber qual quinzena paga guardaria a ' +
      'suposição como dinheiro.',
  },
  espelho_vencido: {
    title: 'O espelho do ERP está velho demais para pagar esta competência.',
    because:
      'As vendas desta competência saíram do espelho de veículos, e ele não é ' +
      'atualizado há mais tempo do que a apuração aceita. Um contrato cancelado ' +
      'no ERP depois da última varredura ainda consta aqui como venda — e a ' +
      'comissão é escolhida pela faixa do total do mês, onde uma placa a mais ' +
      'pode valer o degrau de R$ 350,00 entre 109 e 110 placas.',
    unlock:
      'Varrer a coleção de veículos em Integrações → Saúde do ERP e apurar de ' +
      'novo. A listagem de vendas continua mostrando as linhas enquanto isso, ' +
      'dizendo de quando é o espelho: ver não exige o mesmo frescor que pagar.',
  },
} as const satisfies Record<ReasonNotToSettle, ReasonExplicado>;

/**
 * O motivo explicado, com resposta para o motivo que este mapa NÃO conhece.
 *
 * ## Isto não é paranoia: é um incidente medido, em 09/09
 *
 * O `satisfies Record<ReasonNotToSettle, …>` acima garante que o mapa cobre o
 * vocabulário do contrato **no momento da compilação**. Ele não garante nada
 * sobre o que o servidor **em execução** devolve, e as duas coisas se separam
 * sempre que os dois lados estão em versões diferentes.
 *
 * Aconteceu no dia em que a P5 foi respondida. `criterio_de_ranking_nao_definido`
 * saiu do contrato e saiu deste mapa; o Vite trocou a tela por HMR na hora, e a
 * API continuou de pé servindo um `dist` de horas antes — que ainda empurrava
 * aquele motivo. `REASON[m]` virou `undefined`, `REASON[m].title` estourou, e a
 * tela do ranking **e a de comissões** ficaram brancas. Nenhum teste pega isso:
 * a compilação estava certa nos dois lados, cada um contra a sua versão.
 *
 * Em produção o repositório tem **um deploy só**, então a janela é curta — mas
 * existe, e é justamente durante a troca de réplica. Em desenvolvimento ela é
 * larga: basta o processo velho segurar a porta, que é a primeira das três
 * armadilhas de ambiente do `CLAUDE.md`.
 *
 * ## Por que a saída é dizer o valor cru, e não esconder o item
 *
 * Descartar o motivo desconhecido daria uma lista mais curta sem nada avisando —
 * e no limite uma lista **vazia**, que este componente traduz como "o servidor
 * não nomeou o que falta". Seria a tela afirmando que a casa não sabe por quê,
 * quando o servidor disse por quê e a tela não soube ler. O valor cru é feio e é
 * verdadeiro: quem o vê sabe que precisa recarregar, e quem o reporta traz o
 * nome do motivo junto.
 */
function reasonExplained(
  reason: ReasonNotToSettle,
  instead?: ReasonOverrides,
): ReasonExplicado {
  const known: Partial<Record<ReasonNotToSettle, ReasonExplicado>> = REASON;
  return (
    instead?.[reason] ??
    known[reason] ?? {
      title: `Motivo não reconhecido por esta tela: ${reason}`,
      because:
        'O servidor nomeou algo que esta versão da tela não sabe traduzir. Isso ' +
        'quase sempre significa que o servidor e a tela estão em versões ' +
        'diferentes — em desenvolvimento, uma API que ficou de pé servindo código ' +
        'antigo.',
      unlock:
        'Recarregar a página. Se continuar, a API precisa ser recompilada e ' +
        'reiniciada: o processo velho segurando a porta serve o código de antes, e ' +
        'nada avisa.',
    }
  );
}

/**
 * A lista de motivos, do jeito que toda tela deste módulo a mostra.
 *
 * **Nunca ao lado de um número.** Se há motivo, não há número: é a decisão que
 * as uniões discriminadas do contrato existem para impor, e repeti-la aqui
 * impede a tela de desenhar "R$ 0,00" com uma nota de rodapé embaixo. Zero é um
 * fato sobre o dinheiro de uma pessoa; "a casa não sabe" é outro.
 *
 * Ordem preservada: o servidor manda na ordem de quem destrava — a decisão
 * primeiro, a estrutura depois —, e reordenar aqui perderia isso.
 */
export function WhyNotSettled({
  title,
  reasons,
  instead,
  children,
}: {
  title: string;
  reasons: readonly ReasonNotToSettle[];
  /** A explicação DESTA tela para os motivos que ela mostra — ver `ReasonOverrides`. */
  instead?: ReasonOverrides;
  children?: ReactNode;
}) {
  return (
    <Alert tone="alerta" title={title}>
      {children}
      {/*
       * Lista vazia é caso real: o contrato deixa `motivos` ser `[]` em alguns
       * ramos, e uma lista vazia desenhada como lista seria uma caixa vazia
       * abaixo do título. A frase diz o que se sabe — que não se sabe.
       */}
      {reasons.length === 0 ? (
        <p className="mt-1">
          O servidor não nomeou o que falta. Isso é uma lacuna da resposta, não uma
          resposta: nada aqui deve ser lido como “está tudo certo e deu zero”.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2.5">
          {reasons.map((m) => {
            /*
             * `reasonExplained` e não `REASON[m]` — ver o cabeçalho dele. Um
             * motivo que esta versão da tela não conhece já deixou duas telas
             * brancas, e a diferença entre as duas formas é essa.
             */
            const explained = reasonExplained(m, instead);
            return (
              <li key={m}>
                <p className="font-medium">{explained.title}</p>
                <p className="mt-0.5 text-texto-suave">{explained.because}</p>
                <p className="mt-0.5 text-texto-suave">
                  <span className="rotulo">o que destrava</span> {explained.unlock}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Alert>
  );
}

/* ------------------------------------------------------------------------- *
 * Pagamento — "não sabemos" nunca é "não pago"
 * ------------------------------------------------------------------------- */

/**
 * O que a casa sabe sobre o pagamento de uma comissão.
 *
 * Hoje o único estado é `nao_sabemos`, e o selo diz exatamente isso. A
 * alternativa — um selo cinza escrito "Não pago" — seria uma afirmação sobre o
 * dinheiro de uma pessoa que nenhum caminho deste repositório consegue fazer, e
 * é a mentira mais cara que este módulo pode contar: quem lê conclui que a casa
 * deve, ou que a casa já decidiu não pagar.
 *
 * O tom é `informacao` e não `alerta`: não há nada de errado acontecendo. A
 * verdade sobre pagamento é o ciclo da gestão, e ele ainda não existe.
 *
 * `estado` entra na assinatura mesmo com um valor só. Quando o segundo nascer,
 * o `switch` que falta quebra na compilação — que é o oposto de um estado novo
 * aparecendo na tela com o texto do antigo.
 */
export function PaymentBadge({ payment }: { payment: CommissionPayment }) {
  if (payment.estado === 'nao_sabemos') {
    return <Badge tone="informacao">Pagamento: a casa não sabe</Badge>;
  }
  return null;
}

/** A frase inteira, para onde cabe a explicação e não só o selo. */
export const PAYMENT_UNKNOWN =
  'A plataforma não guarda pagamento de comissão, e não sabe se esta foi paga. ' +
  'Quem vai responder isso é o ciclo de pagamento da gestão, que ainda não ' +
  'existe. Nada nesta tela deve ser lido como “não pago”: os dois são estados ' +
  'diferentes, e só um deles a casa consegue afirmar.';

/* ------------------------------------------------------------------------- *
 * A régua por pessoa — sob qual das duas cada consultor é pago
 * ------------------------------------------------------------------------- */

/* O nome de cada perfil na tela — mora em `lib/`, porque o ERP também vincula. */
export { PROFILE_LABEL };

/**
 * Sob qual régua esta pessoa é paga.
 *
 * A régua é obrigatória desde 22/10/2026 — até ali o nulo era "sem régua
 * declarada", em `alerta`, e travava a apuração da praça inteira. Os perfis
 * não levam tom de erro nem de sucesso: nenhum está certo ou errado, são réguas
 * diferentes. A base de reativação leva `informacao` porque é a que conta
 * outra coisa, e a linha dela não pode ser lida como a de quem não vendeu.
 */
export function ProfileBadge({ profile }: { profile: WireConsultantProfile }) {
  if (profile === 'base_reativacao') {
    return <Badge tone="informacao">{PROFILE_LABEL.base_reativacao}</Badge>;
  }
  return <Badge tone="neutro">{PROFILE_LABEL[profile]}</Badge>;
}

/* ------------------------------------------------------------------------- *
 * As listas que várias telas precisam
 *
 * Consultores e times aparecem em quatro telas — vendas (filtro e casamento),
 * metas (o alvo), comissões (a leitura) e o cadastro. Uma cópia da consulta em
 * cada uma é como uma delas esquece o `tenantId` da chave e passa a mostrar o
 * elenco da outra praça sob o nome desta.
 * ------------------------------------------------------------------------- */

/**
 * Os consultores da base atual.
 *
 * `enabled` existe porque a lista é de **outro recurso**: quem tem
 * `sales.sales:ver` e não tem `sales.teams:ver` recebe 403 aqui.
 * Buscar assim mesmo faria a tela mostrar "não foi possível carregar" — ou, pior,
 * um seletor vazio dizendo que a base não tem consultor — no lugar de dizer qual
 * permissão falta.
 *
 * `includeInactive` traz quem parou de vender: a tela de comissões precisa
 * deles, porque a comissão de março continua sendo de quem a fez.
 */
export function useConsultants({
  enabled = true,
  includeInactive = false,
}: { enabled?: boolean; includeInactive?: boolean } = {}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: ['comercial', 'consultores', tenantId, includeInactive],
    enabled: enabled,
    queryFn: async () => {
      const r = await api.sales.listConsultants({
        query: includeInactive ? { includeInactive: 'true' } : {},
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os consultores desta base'));
      }
      return r.body;
    },
    /*
     * Segura o resultado anterior só dentro da mesma base. O código do
     * voluntário é POR BASE — o mesmo operador é 394 no CE e 75 no GO —, então
     * a lista da praça anterior sob o nome desta mandaria alguém conferir um
     * número contra o código errado do ERP.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });
}

/** Os times da base atual — mesmo desenho e mesmo recurso dos consultores. */
export function useTeams({
  enabled = true,
  includeArchived = false,
}: { enabled?: boolean; includeArchived?: boolean } = {}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: ['comercial', 'times', tenantId, includeArchived],
    enabled: enabled,
    queryFn: async () => {
      const r = await api.sales.listTeams({
        query: includeArchived ? { includeArchived: 'true' } : {},
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os times desta base'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });
}

/**
 * As pessoas da base — para vincular a um código de voluntário, apontar um
 * líder ou abrir uma vigência de superior.
 *
 * A lista é da **plataforma**, sob `platform.users:ver`. Quem não tem essa
 * permissão não recebe um seletor vazio: recebe a frase que diz qual permissão
 * falta, escrita na tela que a usa.
 *
 * A chave repete o cuidado que a tela de demandas registrou: com `''` no fim,
 * ela seria idêntica à da tela de usuários com a busca em branco — mesma entrada
 * de cache, dois `queryFn` diferentes —, e este seletor renderizaria uma página
 * de 50 linhas achando que é a base inteira.
 */
export function useTenantPeople({ enabled = true }: { enabled?: boolean } = {}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: ['plataforma', 'usuarios', tenantId, 'todas-as-pessoas'],
    enabled: enabled,
    queryFn: async () => {
      const r = await api.platform.listUsers({
        query: { page: 1, perPage: 200 },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as pessoas desta base'));
      }
      return r.body;
    },
  });
}

/** Atalho: a mesma pergunta que quatro telas fazem antes de buscar uma lista. */
export function useCanViewRoster(): boolean {
  const { data: context } = useSessionContext();
  return can(context, 'sales.teams', 'ver');
}

/**
 * A frase que uma tela escreve quando a lista de consultores não veio por falta
 * de permissão.
 *
 * Existe como constante porque três telas a dizem, e porque a versão errada dela
 * é sempre a mesma: "nenhum consultor cadastrado nesta base" sobre uma base com
 * dezenas — a tela afirmando um fato que o código nunca chegou a consultar.
 */
export const NO_ROSTER_ACCESS =
  'A lista de consultores vem da permissão “Consultores, times e hierarquia: ' +
  'ver”, que você não tem. Isto não quer dizer que a base não tenha consultores ' +
  '— quer dizer que esta tela não pode perguntar. Peça a quem administra a ' +
  'plataforma, em Grupos.';

/*
 * Reexportado, e não redeclarado: os dois moram em `components/Values.tsx`
 * porque dois módulos precisavam deles e a saída fácil tinha sido copiar.
 */
export { MoneyValue } from '../../components/Values.js';

/**
 * A recusa do servidor, dita como ela chegou.
 *
 * O corpo de erro da casa tem sempre `message`, e ela é escrita para quem lê —
 * a do código de boleto repetido diz o que fazer. Trocá-la por "não foi
 * possível salvar" jogaria fora a única frase útil da resposta.
 *
 * Mora aqui desde 21/09/2026, quando a segunda tela do módulo precisou dela.
 * O `what` é parâmetro porque a frase de reserva nomeia o que falhou — "a
 * premiação", "a régua" —, e uma cópia por tela é como dois formatos para a
 * mesma mensagem começam.
 */
export function refusalOf(r: { status: number; body: unknown }, what: string): string {
  const body = r.body;
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = (body as { message: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return loadFailureMessage(r.status, what);
}

/**
 * O que falta declarar, dito para quem lê — e não o valor do enum.
 *
 * Quatro são **cadastro que ninguém preencheu** e dois são **estado do
 * espelho** — nenhum é defeito. Quem vê a frase precisa saber o que fazer, e
 * `tipos_de_boleto_nao_declarados` na tela não diz onde se escolhem os tipos.
 *
 * A frase da tarifa não manda ninguém preencher nada, e é de propósito: não há
 * tela onde declarar o que o banco cobrou. Ela nomeia a competência como antiga
 * porque é o que ela é — de 2024 em diante o espelho da CE tem a tarifa em
 * 100% dos boletos pagos.
 */
export function refusalLabel(
  reason: Extract<AwardLine, { estado: 'recusado' }>['reason'],
): string {
  if (reason === 'tipos_de_boleto_nao_declarados') {
    return 'falta escolher os tipos de boleto';
  }
  if (reason === 'faixas_nao_declaradas') {
    return 'falta cadastrar as faixas de placas';
  }
  if (reason === 'tipos_de_veiculo_nao_declarados') {
    return 'falta escolher os tipos de veículo';
  }
  if (reason === 'tipos_de_adesao_nao_declarados') {
    return 'falta escolher os tipos de adesão';
  }
  if (reason === 'tarifa_do_banco_ausente') {
    return 'há boleto sem a tarifa do banco registrada no ERP';
  }
  /* As duas da premiação POR PRODUTO (14/10/2026). */
  if (reason === 'produtos_nao_declarados') {
    return 'falta escolher os produtos e o valor de cada um';
  }
  if (reason === 'produtos_nao_consultados') {
    return 'há veículo cujos produtos ainda não foram consultados no ERP';
  }
  /*
   * As da premiação de LÍDER, e as cinco apontam para cadastro de time ou de
   * concessão — nunca para a esteira. Até 23/09 elas caíam na frase do fim
   * desta função, que falava de varredura de boletos: a tela mandava conferir
   * o espelho sobre o que era um time sem líder.
   */
  if (reason === 'time_sem_integrante_na_competencia') {
    return 'ninguém respondia ao líder nesta competência';
  }
  if (reason === 'liderado_fora_do_time_na_competencia') {
    return 'nesta competência, esta pessoa não respondia ao líder que repartiu';
  }
  if (reason === 'consultor_do_time_sem_codigo_no_erp') {
    return 'alguém do time está sem código do ERP';
  }
  if (reason === 'boleto_de_dois_membros_do_time') {
    return 'há boleto na carteira de dois membros do time';
  }
  if (reason === 'concessao_acima_do_teto_da_premiacao') {
    return 'a parte repartida ficou acima do percentual da premiação';
  }
  /*
   * Esta manda para a Configuração da base, e NÃO para o cadastro da premiação.
   *
   * O que conta como perda é régua da praça inteira, e não daquela premiação —
   * a frase precisa dizer onde se conserta, senão quem a lê vai editar a
   * premiação e não encontrar o campo.
   */
  if (reason === 'tipos_de_boleto_perdidos_nao_declarados') {
    return 'falta a base declarar o que conta como perda, em Configurações';
  }
  /*
   * `espelho_de_boleto_incompleto`. Desde 27/10/2026 a porta não recusa mais a
   * leitura de tela por esteira que nunca rodou — a premiação sai zerada e
   * marcada como piso —, então o que sobra aqui é o espelho que não pôde ser
   * lido, e a frase diz só isso.
   */
  return 'o espelho de boletos desta base não pôde ser lido agora';
}

/**
 * O seletor dos dois lados.
 *
 * ## Dois botões, e não um `Select`
 *
 * São duas opções, sempre as mesmas, e a escolha é o assunto da tela — não um
 * refinamento dentro dela. Um `Select` esconderia metade do produto atrás de um
 * clique e de uma lista, e quem abrisse a tela veria um dos lados sem saber que
 * o outro existe.
 *
 * ## A cor está no ESTADO do botão, e não no rótulo
 *
 * O lado escolhido é `primario`; o outro é `contorno`. O vermelho do "perdendo"
 * mora no bloco de total, que é onde o número está — pintar o botão de vermelho
 * o tempo todo faria a tela parecer em erro mesmo para quem está apenas olhando
 * o outro lado.
 *
 * `aria-pressed` e não `aria-current`: são dois botões que ligam e desligam uma
 * leitura, não dois lugares para onde ir. Quem navega por leitor de tela ouve
 * qual está apertado, que é a pergunta que a cor responde para quem vê.
 */
export function StateSwitch({
  current,
  onChange,
  own,
}: {
  current: AwardState;
  onChange: (state: AwardState) => void;
  /**
   * `true` na PRÓPRIA carteira, e só nela os botões falam em "você".
   *
   * Na tabela de todas as carteiras e na carteira de outra pessoa, "Você já
   * garantiu" dizia a quem administra que o dinheiro era dele — apontado pelo
   * dono em 25/09/2026. Ali o rótulo nomeia o lado, e não quem o tem.
   */
  own: boolean;
}) {
  return (
    <div
      className="flex items-center gap-2"
      role="group"
      aria-label="Lado da competência"
    >
      <Button
        variant={current === 'garantido' ? 'primario' : 'contorno'}
        icon="confirmado"
        aria-pressed={current === 'garantido'}
        onClick={() => onChange('garantido')}
      >
        {own ? 'Você já garantiu' : 'Garantido'}
      </Button>
      <Button
        variant={current === 'perdendo' ? 'primario' : 'contorno'}
        icon="atencao"
        aria-pressed={current === 'perdendo'}
        onClick={() => onChange('perdendo')}
      >
        {own ? 'Você está perdendo' : 'Em risco'}
      </Button>
    </div>
  );
}

/**
 * Por quanto tempo uma leitura de PREMIAÇÃO vale sem perguntar de novo — o
 * cache das telas de BI, Extrato e Início (29/10/2026).
 *
 * A apuração é a conta mais cara da API, e sem isto cada volta à tela (ou ao
 * mês já visto na faixa) refazia todas as leituras: o padrão do React Query é
 * `staleTime: 0`. Cinco minutos é o mesmo `gcTime` do cliente — o mês visto
 * volta na hora enquanto ainda está guardado. Fechar, recalcular e dar baixa
 * invalidam a chave como sempre, e não esperam o prazo.
 */
export const AWARD_STALE_MS = 5 * 60_000;

type MonthStatus = 'closed' | 'current' | 'next';

// @valor
const MONTH_STATUS: Record<MonthStatus, { label: string; tone: MonthStripTone }> = {
  closed: { label: 'Fechado', tone: 'sucesso' },
  current: { label: 'Atual', tone: 'alerta' },
  next: { label: 'Próximo', tone: 'neutro' },
};

/**
 * Os doze meses do ano, pelo mês em que se RECEBE — a convenção do filtro de
 * recebimento (`receiptOptions`): o botão "Out" é a competência de setembro,
 * que se recebe em outubro. Por isso "Atual" cai no mês do calendário, e a tela
 * sem mês escolhido abre nele (`awardPeriod`).
 *
 * - **Fechado**: o recebimento já passou. A referência dizia "Pago", e a casa
 *   não sabe se pagou — o saldo distingue competência fechada de competência
 *   paga —, então a pílula diz só o que é verdade da data.
 * - **Atual**: o mês corrente de recebimento.
 * - **Próximo**: o seguinte (competência do mês corrente, ainda correndo) abre
 *   e mostra o acumulado; os depois dele ficam desabilitados, porque a
 *   competência deles nem começou e a tela mostraria zero como resultado.
 *
 * O ano é o do mês escolhido: um link antigo com `?month=` abre a faixa do ano
 * dele, e não a deste.
 */
function receiptMonthItems(competence: Period): MonthStripItem[] {
  const nowReceipt = currentPeriod();
  const lastOpen = shiftPeriod(nowReceipt, 1);
  const selectedReceipt = shiftPeriod(competence, 1);
  const year = selectedReceipt.slice(0, 4);

  return Array.from({ length: 12 }, (_, i) => {
    const receipt = `${year}-${String(i + 1).padStart(2, '0')}`;
    const status: MonthStatus =
      receipt < nowReceipt ? 'closed' : receipt === nowReceipt ? 'current' : 'next';
    return {
      /* A chave é a competência, que é o que a rota entende. */
      key: shiftPeriod(receipt, -1),
      label: monthName(receipt).slice(0, 3),
      status: MONTH_STATUS[status].label,
      tone: MONTH_STATUS[status].tone,
      selected: receipt === selectedReceipt,
      disabled: receipt > lastOpen,
      description: `Recebe em ${periodLabel(receipt)}, competência de ${periodLabel(shiftPeriod(receipt, -1))}`,
    };
  });
}

/**
 * O MÊS DE RECEBIMENTO das telas de premiação — a faixa de meses do Início
 * (28/10/2026), e desde 29/10/2026 também a das Métricas e do Extrato, no
 * lugar do par "mês atual / próximo" (`ReceiptSwitch`).
 *
 * Os botões são só datas: nenhum mês é lido antes do clique, e a tela abre
 * pedindo apenas o mês escolhido. `loading` é o mês novo ainda chegando.
 */
export function ReceiptMonthStrip({
  current,
  onChange,
  loading = false,
}: {
  /** A competência escolhida — o valor que a rota entende. */
  current: Period;
  onChange: (period: Period) => void;
  loading?: boolean;
}) {
  return (
    <MonthStrip
      label="Mês de recebimento"
      items={receiptMonthItems(current)}
      loading={loading}
      onSelect={(key) => onChange(key as Period)}
    />
  );
}

/**
 * A placa como link para a ficha do veículo na carteira de clientes —
 * `/sales/portfolio/members/$code?vehicle=…&view=boletos` (28/10/2026, pedido
 * do dono). O `consultant` viaja junto: sem ele, a placa de outra pessoa
 * abriria na agenda de quem está olhando, que não tem aquele cliente.
 *
 * Sem os códigos do ERP não há ficha para abrir, e a placa fica como texto.
 */
export function PlateLink({
  plate,
  memberCode,
  vehicleCode,
  consultant,
}: {
  plate: string;
  memberCode: string | null;
  vehicleCode: string | null;
  consultant: string | undefined;
}) {
  if (memberCode === null || vehicleCode === null) return <>{plate}</>;
  return (
    <Link
      to="/sales/portfolio/members/$code"
      params={{ code: memberCode }}
      search={{ consultant, vehicle: vehicleCode, view: 'boletos' }}
      state={{ backLabel: 'Relatório da premiação' }}
      className={linkTextClasses()}
    >
      {plate}
    </Link>
  );
}
